import React, { useState, useEffect } from 'react';
import apiService from '../utils/api';
import Sidebar from './sidebar/Sidebar';
import './Departments.css';
import Loader from './Loader';
import Snackbar from '@mui/material/Snackbar';
import Delete from '@mui/icons-material/Delete';
import Breadcrumbs from './Breadcrumbs';

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Add modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [newDepartmentDescription, setNewDepartmentDescription] = useState('');
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDepartmentName, setEditDepartmentName] = useState('');
  const [editDepartmentDescription, setEditDepartmentDescription] = useState('');
  
  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteDepartmentId, setDeleteDepartmentId] = useState(null);

  // Snackbar notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const fetchDepartments = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.fetchData('departments');
      const rawData = Array.isArray(response) ? response : (response?.data || []);
      const formatted = rawData.map((dept, index) => ({
        id: dept.id || dept.DepartmentId,
        name: dept.name || dept.DepartmentName || dept.value || '',
        description: dept.description || 'No description available',
        displayIndex: index + 1
      }));
      setDepartments(formatted);
      setFilteredDepartments(formatted);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError('Failed to load departments. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = departments.filter(dept =>
      dept.name.toLowerCase().includes(query) ||
      (dept.description && dept.description.toLowerCase().includes(query))
    );
    setFilteredDepartments(filtered);
  }, [searchQuery, departments]);

  const handleAddDepartment = async () => {
    if (!newDepartmentName.trim()) {
      setSnackbarMessage('Department name is required');
      setIsSuccess(false);
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        name: newDepartmentName.trim(),
        description: newDepartmentDescription.trim() || 'No Description',
        created_by: sessionStorage.getItem('user_id') || 'system',
        updated_by: sessionStorage.getItem('user_id') || 'system'
      };
      const response = await apiService.postData('departments', payload);
      if (response.status === 'success' || response.DepartmentId || response.id) {
        await fetchDepartments();
        setNewDepartmentName('');
        setNewDepartmentDescription('');
        setIsModalOpen(false);
        setSnackbarMessage('Department added successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
      } else {
        throw new Error(response.message || 'Failed to add department');
      }
    } catch (err) {
      console.error('Error adding department:', err);
      const errorMessage = err.response?.status === 409
        ? 'Department already exists'
        : (err.response?.data?.message || err.message || 'Failed to add department. Please try again later.');
      setSnackbarMessage(errorMessage);
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEdit = async (id) => {
    if (!editDepartmentName.trim()) {
      setSnackbarMessage('Department name is required');
      setIsSuccess(false);
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const originalDept = departments.find(dept => dept.id === id);
      if (originalDept && originalDept.name === editDepartmentName.trim() && originalDept.description === editDepartmentDescription.trim()) {
        setSnackbarMessage('No changes detected');
        setIsSuccess(false);
        setSnackbarOpen(true);
        setIsLoading(false);
        return;
      }

      const payload = {
        name: editDepartmentName.trim(),
        description: editDepartmentDescription.trim(),
        updated_by: sessionStorage.getItem('user_id') || 'system'
      };

      const response = await apiService.putData(`departments/${id}`, payload);
      if (response.status === 'success' || response.id) {
        await fetchDepartments();
        setSnackbarMessage('Department updated successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
        setIsEditModalOpen(false);
        setEditingId(null);
        setEditDepartmentName('');
        setEditDepartmentDescription('');
      } else {
        throw new Error(response.message || 'Failed to update department');
      }
    } catch (err) {
      console.error('Error updating department:', err);
      const errorMessage = err.response?.status === 409
        ? 'Department with this name already exists'
        : (err.response?.data?.message || err.message || 'Failed to update department. Please try again later.');
      setSnackbarMessage(errorMessage);
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDepartment = async (id) => {
    try {
      setIsLoading(true);
      const response = await apiService.deleteData(`departments/${id}`);
      if (response.status === 'success' || response.id) {
        await fetchDepartments();
        setSnackbarMessage('Department deleted successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
      } else {
        throw new Error(response.message || 'Failed to delete department');
      }
    } catch (err) {
      console.error('Error deleting department:', err);
      setSnackbarMessage(err.response?.data?.message || err.message || 'Failed to delete department. Please try again later.');
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setDeleteDepartmentId(null);
    }
  };

  return (
    <div className="departments-page-container">
      <Sidebar />
      <div className="departments-page-content">
        <Breadcrumbs />
        <div className="departments-header">
          <h2 style={{ color: '#ff6b35', fontSize: '24px' }}>Departments</h2>
          <button
            className="add-department-button"
            onClick={() => setIsModalOpen(true)}
          >
            Add Department
          </button>
        </div>

        {isLoading && departments.length === 0 ? (
          <Loader isLoading={isLoading} message="Loading Departments..." color="#ff6b35" />
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search departments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <svg
                className="departments-search-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>

            <table className="departments-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Department Name</th>
                  <th>Department Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDepartments.length > 0 ? (
                  filteredDepartments.map((dept, index) => (
                    <tr key={dept.id}>
                      <td>{index + 1}</td>
                      <td>{dept.name}</td>
                      <td>{dept.description || 'No description available'}</td>
                      <td>
                        <svg
                          className="edit-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#ff6b35"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ cursor: 'pointer', marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }}
                          title="Edit Department"
                          onClick={() => {
                            setIsEditModalOpen(true);
                            setEditingId(dept.id);
                            setEditDepartmentName(dept.name);
                            setEditDepartmentDescription(dept.description === 'No description available' ? '' : dept.description);
                          }}
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        <Delete
                          style={{
                            cursor: 'pointer',
                            width: '20px',
                            height: '20px',
                            color: '#ff6b35',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                          }}
                          titleAccess="Delete Department"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeleteDepartmentId(dept.id);
                            setIsDeleteModalOpen(true);
                          }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">No departments found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}

        {/* Add Modal */}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="departments-modal-content">
              <div className="departments-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Add Department</h3>
              </div>
              <div className="departments-modal-form">
                <label htmlFor="department-name" className="department-label">Department Name</label>
                <input
                  id="department-name"
                  type="text"
                  value={newDepartmentName}
                  onChange={(e) => setNewDepartmentName(e.target.value)}
                  placeholder="Enter department name"
                  className="modal-input"
                />
                <label htmlFor="department-description" className="department-label" style={{ marginTop: '12px' }}>Description</label>
                <input
                  id="department-description"
                  type="text"
                  value={newDepartmentDescription}
                  onChange={(e) => setNewDepartmentDescription(e.target.value)}
                  placeholder="Enter description"
                  className="modal-input"
                />
              </div>
              <div className="modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={handleAddDepartment}
                >
                  Save
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
                    setNewDepartmentName('');
                    setNewDepartmentDescription('');
                    setIsModalOpen(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && (
          <div className="modal-overlay">
            <div className="departments-modal-content">
              <div className="departments-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Update Department</h3>
              </div>
              <div className="departments-modal-form">
                <label htmlFor="edit-department-name" className="department-label">Department Name</label>
                <input
                  id="edit-department-name"
                  type="text"
                  value={editDepartmentName}
                  onChange={(e) => setEditDepartmentName(e.target.value)}
                  placeholder="Enter department name"
                  className="modal-input"
                />
                <label htmlFor="edit-department-description" className="department-label" style={{ marginTop: '12px' }}>Description</label>
                <input
                  id="edit-department-description"
                  type="text"
                  value={editDepartmentDescription}
                  onChange={(e) => setEditDepartmentDescription(e.target.value)}
                  placeholder="Enter description"
                  className="modal-input"
                />
              </div>
              <div className="modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={() => handleSaveEdit(editingId)}
                >
                  Save
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingId(null);
                    setEditDepartmentName('');
                    setEditDepartmentDescription('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="modal-overlay">
            <div className="departments-modal-content">
              <div className="departments-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Confirm Delete</h3>
              </div>
              <div className="departments-modal-form">
                <p>Do you want to delete this department?</p>
              </div>
              <div className="modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={() => handleDeleteDepartment(deleteDepartmentId)}
                >
                  Delete
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setDeleteDepartmentId(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: isSuccess ? '#4caf50' : '#f44336',
            },
            zIndex: 1400,
          }}
        />
      </div>
    </div>
  );
};

export default Departments;
